/**
 * Singleton WebSocket client for real-time task status updates.
 *
 * Manages a single connection to /ws/tasks, aggregates subscriptions
 * from multiple hook instances, and dispatches incoming updates to callbacks.
 *
 * Keepalive: the server sends periodic {"type":"ping"} messages. If no message
 * arrives within STALE_TIMEOUT_MS the connection is considered dead and replaced.
 */
import type { SnapshotTaskStatus } from "./api/data-hub/types";
import {
  buildWebSocketUrl,
  shouldReconnectAfterWebSocketClose,
} from "@/utils/wsAuth";

type TaskUpdateCallback = (update: SnapshotTaskStatus) => void;
type SubscribeDeniedCallback = (taskIds: string[]) => void;

interface Subscription {
  taskIds: Set<string>;
  callback: TaskUpdateCallback;
  onDenied?: SubscribeDeniedCallback;
}

const LOG = "[TaskWS]";
const RECONNECT_BASE_MS = 2000;
const MAX_RECONNECT_DELAY_MS = 30000;

/** If no message (including pings) arrives within this window, force-reconnect. */
const STALE_TIMEOUT_MS = 90_000;

function _getWsUrl(): string {
  const base = import.meta.env.VITE_API_BASE_URL || "";

  let wsUrl: string;
  if (base.startsWith("http")) {
    const url = new URL(base);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = "/ws/tasks";
    wsUrl = url.toString();
  } else {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    wsUrl = `${proto}//${window.location.host}/ws/tasks`;
  }

  return buildWebSocketUrl(wsUrl);
}

function _shortIds(ids: string[] | Set<string>): string {
  const arr = [...ids];
  if (arr.length <= 3) return arr.map((id) => id.slice(0, 8)).join(", ");
  return `${arr.slice(0, 2).map((id) => id.slice(0, 8)).join(", ")} +${arr.length - 2} more`;
}


class TaskWebSocket {
  private ws: WebSocket | null = null;
  private subscriptions = new Map<number, Subscription>();
  private nextId = 0;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private staleTimer: ReturnType<typeof setTimeout> | null = null;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private aggregatedTaskIds = new Set<string>();
  private connectionListeners = new Set<() => void>();

  /** Subscribe to updates for a set of task IDs. Returns an unsubscribe function. */
  subscribe(
    taskIds: string[],
    callback: TaskUpdateCallback,
    options?: { onDenied?: SubscribeDeniedCallback },
  ): () => void {
    if (taskIds.length === 0) return () => {};

    const id = this.nextId++;
    this.subscriptions.set(id, {
      taskIds: new Set(taskIds),
      callback,
      onDenied: options?.onDenied,
    });
    console.info(LOG, `Subscribe #${id}: ${_shortIds(taskIds)} (${this.subscriptions.size} active)`);

    this._syncSubscriptions();
    this._ensureConnected();

    return () => {
      this.subscriptions.delete(id);
      console.info(LOG, `Unsubscribe #${id} (${this.subscriptions.size} remaining)`);
      this._syncSubscriptions();
    };
  }

  get isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /** True while a handshake is in progress — callers should wait before HTTP fallback. */
  get isConnecting(): boolean {
    return this.ws?.readyState === WebSocket.CONNECTING;
  }

  /** WebSocket is open or connecting; HTTP poll only when fully disconnected. */
  get isLive(): boolean {
    return this.isConnected || this.isConnecting;
  }

  /** Notify React hooks when the socket opens or closes (for HTTP fallback polling). */
  onConnectionChange(listener: () => void): () => void {
    this.connectionListeners.add(listener);
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  // --- Connection lifecycle ---

  private _notifyConnectionChange(): void {
    for (const listener of this.connectionListeners) {
      try {
        listener();
      } catch (e) {
        console.error(LOG, "Connection listener error:", e);
      }
    }
  }

  private _ensureConnected(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (this.subscriptions.size === 0) return;
    this._connect();
  }

  private _connect(): void {
    let url: string;
    try {
      url = _getWsUrl();
    } catch (e) {
      console.error(LOG, "Failed to build WebSocket URL:", e);
      return;
    }

    console.info(LOG, `Connecting to ${url.split("?")[0]}`);

    try {
      this.ws = new WebSocket(url);
    } catch (e) {
      console.error(LOG, "Failed to create WebSocket:", e);
      this.ws = null;
      this._scheduleReconnect();
      return;
    }

    this._notifyConnectionChange();

    let hadOpened = false;
    this.ws.onopen = () => {
      hadOpened = true;
      this.reconnectAttempt = 0;
      console.info(LOG, `Connected (${this.aggregatedTaskIds.size} task IDs to subscribe)`);

      if (this.aggregatedTaskIds.size > 0) {
        this._sendSubscribe([...this.aggregatedTaskIds]);
      }

      this._resetStaleTimer();
      this._notifyConnectionChange();
    };

    this.ws.onmessage = (event) => {
      this._resetStaleTimer();

      try {
        const data = JSON.parse(String(event.data));

        // Server ping — reply with pong to keep the connection alive
        if (data?.type === "ping") {
          this._sendPong();
          return;
        }

        if (data?.type === "subscribe_denied") {
          const denied = Array.isArray(data.task_ids)
            ? data.task_ids.filter((id: unknown): id is string => typeof id === "string")
            : [];
          if (denied.length > 0) {
            console.warn(
              LOG,
              `Subscribe denied for ${denied.length} task(s) (missing source_id or wrong company) — HTTP poll only for those IDs`,
            );
            this._notifySubscribeDenied(denied);
          }
          return;
        }

        const update = data as SnapshotTaskStatus;
        if (!update || !update.task_id) {
          console.warn(LOG, "Received message without task_id, ignoring");
          return;
        }
        console.debug(LOG, `← ${update.task_id.slice(0, 8)} status=${update.status} progress=${update.progress ?? "?"}`);
        this._dispatch(update);
      } catch (e) {
        console.warn(LOG, "Failed to parse incoming message:", e);
      }
    };

    this.ws.onclose = (event) => {
      const reason = event.reason || `code ${event.code}`;
      console.info(LOG, `Disconnected (${reason})`);
      this.ws = null;
      this._clearStaleTimer();
      this._notifyConnectionChange();
      if (shouldReconnectAfterWebSocketClose(event?.code ?? 0, event?.reason ?? "", { hadOpened })) {
        this._scheduleReconnect();
      } else {
        console.warn(
          LOG,
          `Not reconnecting after close code ${event.code} (auth failure or normal close)`
        );
      }
    };

    this.ws.onerror = () => {
      // onclose fires after onerror — logging happens there
    };
  }

  private _scheduleReconnect(): void {
    if (this.subscriptions.size === 0) {
      console.debug(LOG, "No active subscriptions, skipping reconnect");
      return;
    }
    if (this.reconnectTimer) return;

    const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempt), MAX_RECONNECT_DELAY_MS);
    this.reconnectAttempt++;
    console.info(LOG, `Reconnecting in ${Math.round(delay / 1000)}s (attempt ${this.reconnectAttempt})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.subscriptions.size > 0) this._connect();
    }, delay);
  }

  // --- Staleness detection ---

  private _resetStaleTimer(): void {
    this._clearStaleTimer();
    if (this.subscriptions.size === 0) return;

    this.staleTimer = setTimeout(() => {
      console.warn(LOG, `No messages for ${STALE_TIMEOUT_MS / 1000}s, force-reconnecting`);
      this._forceReconnect();
    }, STALE_TIMEOUT_MS);
  }

  private _clearStaleTimer(): void {
    if (this.staleTimer) {
      clearTimeout(this.staleTimer);
      this.staleTimer = null;
    }
  }

  private _forceReconnect(): void {
    this._clearStaleTimer();
    if (this.ws) {
      try { this.ws.close(); } catch (_e: unknown) { /* already closed */ }
      this.ws = null;
    }
    this.reconnectAttempt = 0;
    this._scheduleReconnect();
  }

  // --- Subscription management ---

  private _syncSubscriptions(): void {
    const newIds = new Set<string>();
    for (const sub of this.subscriptions.values()) {
      for (const id of sub.taskIds) newIds.add(id);
    }

    const toSubscribe = [...newIds].filter((id) => !this.aggregatedTaskIds.has(id));
    const toUnsubscribe = [...this.aggregatedTaskIds].filter((id) => !newIds.has(id));
    this.aggregatedTaskIds = newIds;

    if (this.ws?.readyState === WebSocket.OPEN) {
      if (toSubscribe.length > 0) this._sendSubscribe(toSubscribe);
      if (toUnsubscribe.length > 0) this._sendUnsubscribe(toUnsubscribe);
    }

    // Defer disconnect so rapid unsubscribe/re-subscribe cycles (e.g. child
    // task chaining) don't tear down the connection between React commits.
    if (this.subscriptions.size === 0) {
      if (!this.disconnectTimer) {
        this.disconnectTimer = setTimeout(() => {
          this.disconnectTimer = null;
          if (this.subscriptions.size === 0) this._disconnect();
        }, 200);
      }
    } else if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
  }

  private _disconnect(): void {
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._clearStaleTimer();
    if (this.ws) {
      console.info(LOG, "Closing idle connection");
      try { this.ws.close(); } catch (_e: unknown) { /* already closed */ }
      this.ws = null;
    }
  }

  // --- Send helpers ---

  private _sendSubscribe(taskIds: string[]): void {
    const msg = JSON.stringify({ action: "subscribe", task_ids: taskIds });
    console.info(LOG, `→ subscribe ${_shortIds(taskIds)}`);
    try { this.ws?.send(msg); } catch (e) {
      console.warn(LOG, "Failed to send subscribe:", e);
    }
  }

  private _sendUnsubscribe(taskIds: string[]): void {
    const msg = JSON.stringify({ action: "unsubscribe", task_ids: taskIds });
    console.info(LOG, `→ unsubscribe ${_shortIds(taskIds)}`);
    try { this.ws?.send(msg); } catch (e) {
      console.warn(LOG, "Failed to send unsubscribe:", e);
    }
  }

  private _sendPong(): void {
    try { this.ws?.send(JSON.stringify({ action: "pong" })); } catch (_e: unknown) { /* best effort */ }
  }

  private _notifySubscribeDenied(taskIds: string[]): void {
    for (const sub of this.subscriptions.values()) {
      const relevant = taskIds.filter((id) => sub.taskIds.has(id));
      if (relevant.length === 0) continue;
      try {
        sub.onDenied?.(relevant);
      } catch (e) {
        console.error(LOG, "onDenied callback error:", e);
      }
    }
  }

  // --- Dispatch ---

  private _dispatch(update: SnapshotTaskStatus): void {
    const taskId = update.task_id;
    if (!taskId) {
      console.warn(LOG, "Ignoring WebSocket update without task_id");
      return;
    }
    let dispatched = 0;

    for (const sub of this.subscriptions.values()) {
      if (!sub.taskIds.has(taskId)) continue;
      try {
        sub.callback(update);
        dispatched++;
      } catch (e) {
        console.error(LOG, `Callback error for task ${taskId.slice(0, 8)}:`, e);
      }
    }

    if (dispatched === 0) {
      console.debug(LOG, `No subscribers for task ${taskId.slice(0, 8)}, ignoring update`);
    }
  }
}

/** Singleton instance */
export const taskWebSocket = new TaskWebSocket();
